import { calculateTotalDuration } from "./durations";

export interface TimelineEntry {
  percent: number;
  /**
   * Bar extent in pixels relative to the zero baseline: positive grows up from
   * zero, negative grows down. Magnitude is the visible bar length. (For the
   * common all-positive case zero sits at the bottom, so this is just the
   * old bottom-anchored height.)
   */
  height: number;
}

/**
 * Y pixel of the zero baseline within the chart area. For all-positive data
 * (min clamped to 0) this is the bottom edge; for all-negative data (max
 * clamped to 0) it is the top edge; mixed data places it in between.
 */
export function computeZeroBaselineY(
  min: number,
  max: number,
  height: number,
  padding: number,
): number {
  const range = max - min || 1;
  const usableHeight = Math.max(0, height - padding * 2);
  return height - padding - ((0 - min) / range) * usableHeight;
}

/**
 * Convert a signed bar extent (see {@link TimelineEntry.height}) plus the zero
 * baseline into the SVG `y`/`height` a `<rect>` needs (height is always >= 0).
 */
export function signedExtentToRect(
  signed: number,
  zeroY: number,
): { y: number; height: number } {
  const visibleHeight = Math.abs(signed);
  const y = signed >= 0 ? zeroY - signed : zeroY;
  return { y, height: visibleHeight };
}

export function buildKeyframeTimeline(
  values: number[],
  min: number,
  max: number,
  height: number,
  padding: number,
  holdStart: number,
  holdEnd: number,
  duration: number,
  advanceDelay: number,
): TimelineEntry[] {
  const range = max - min || 1;
  const usableHeight = Math.max(0, height - padding * 2);
  // Signed pixel extent from the zero baseline. This is independent of `min`
  // because it is measured from zero, not from the domain floor: (v/range)*U.
  // Floor the magnitude at 1px in the bar's own direction so tiny values stay
  // visible (positive => >= 1px up, negative => <= 1px down).
  const toPixels = (v: number) => {
    const extent = (v / range) * usableHeight;
    return extent >= 0 ? Math.max(1, extent) : Math.min(-1, extent);
  };

  const entries: TimelineEntry[] = [];
  const frameCount = values.length;
  const startHold = Math.max(0, Math.min(holdStart, 100));
  const endHold = Math.max(0, Math.min(holdEnd, 100 - startHold));
  const startHoldMs = (startHold / 100) * duration;
  const endHoldMs = (endHold / 100) * duration;
  const animatedDuration = Math.max(0, duration - startHoldMs - endHoldMs);
  const stepTime = frameCount > 1 ? animatedDuration / (frameCount - 1) : 0;
  const pause = Math.max(0, advanceDelay);
  const totalDuration = calculateTotalDuration(
    duration,
    advanceDelay,
    frameCount,
  );

  const toPercent = (timeMs: number) => (timeMs / totalDuration) * 100;

  const firstHeight = toPixels(values[0]);
  entries.push({ percent: 0, height: firstHeight });
  if (startHoldMs > 0) {
    entries.push({ percent: toPercent(startHoldMs), height: firstHeight });
  }

  let currentTime = startHoldMs;
  for (let index = 1; index < frameCount; index++) {
    const prevHeight = toPixels(values[index - 1]);
    if (pause > 0) {
      currentTime += pause;
      entries.push({ percent: toPercent(currentTime), height: prevHeight });
    }

    currentTime += stepTime;
    entries.push({
      percent: toPercent(currentTime),
      height: toPixels(values[index]),
    });
  }

  const lastHeight = toPixels(values[frameCount - 1]);
  if (endHoldMs > 0) {
    const endHoldStart = totalDuration - endHoldMs;
    entries.push({ percent: toPercent(endHoldStart), height: lastHeight });
    entries.push({ percent: 100, height: lastHeight });
  }

  if (!entries.some((entry) => entry.percent === 0)) {
    entries.unshift({ percent: 0, height: firstHeight });
  }
  if (!entries.some((entry) => entry.percent === 100)) {
    entries.push({ percent: 100, height: lastHeight });
  }

  return entries.filter(
    (entry, index, arr) =>
      index === 0 || entry.percent !== arr[index - 1].percent,
  );
}

export function applySequentialIntro(
  timeline: TimelineEntry[],
  keyIndex: number,
  keyCount: number,
  introPercentTotal: number,
) {
  if (
    !Array.isArray(timeline) ||
    timeline.length === 0 ||
    !Number.isFinite(introPercentTotal) ||
    introPercentTotal <= 0 ||
    keyCount <= 0
  ) {
    return timeline;
  }

  const segment = introPercentTotal / keyCount;
  if (!Number.isFinite(segment) || segment <= 0) {
    return timeline;
  }

  const firstHeight = timeline[0]?.height ?? 0;
  const introStart = segment * keyIndex;
  const introEnd = introStart + segment;
  const introEntries: TimelineEntry[] = [{ percent: 0, height: 0 }];
  if (introStart > 0) {
    introEntries.push({ percent: introStart, height: 0 });
  }
  introEntries.push({ percent: introEnd, height: firstHeight });
  introEntries.push({ percent: introPercentTotal, height: firstHeight });

  const scaledTimeline = timeline.map(({ percent, height }) => ({
    percent: introPercentTotal + (100 - introPercentTotal) * (percent / 100),
    height,
  }));

  const combined = [...introEntries, ...scaledTimeline];
  return combined.filter(
    (entry, index, arr) =>
      index === 0 ||
      entry.percent !== arr[index - 1].percent ||
      entry.height !== arr[index - 1].height,
  );
}

export function applySequentialTransitions(
  timeline: TimelineEntry[],
  keyIndex: number,
  keyCount: number,
  skipUntilPercent = 0,
) {
  if (!Array.isArray(timeline) || timeline.length === 0 || keyCount <= 1) {
    return timeline;
  }

  const EPS = 0.0001;
  const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);
  const pushEntry = (arr: TimelineEntry[], entry?: TimelineEntry) => {
    if (!entry) return;
    const percent = Number(entry.percent);
    const height = Number(entry.height);
    const last = arr[arr.length - 1];
    if (last && Math.abs(last.percent - percent) < EPS) {
      last.height = height;
    } else {
      arr.push({ percent, height });
    }
  };

  const adjusted: TimelineEntry[] = [];
  for (let i = 0; i < timeline.length - 1; i++) {
    const current = timeline[i];
    const next = timeline[i + 1];
    pushEntry(adjusted, current);

    const span = next.percent - current.percent;
    if (span <= EPS || next.percent <= skipUntilPercent + EPS) {
      continue;
    }

    const delta = next.height - current.height;
    if (Math.abs(delta) < EPS) {
      continue;
    }

    if (
      skipUntilPercent - current.percent > EPS &&
      skipUntilPercent < next.percent - EPS
    ) {
      pushEntry(adjusted, {
        percent: skipUntilPercent,
        height: current.height,
      });
    }

    const effectiveStart = Math.max(current.percent, skipUntilPercent);
    const effectiveSpan = next.percent - effectiveStart;
    if (effectiveSpan <= EPS) {
      continue;
    }

    const segment = effectiveSpan / keyCount;
    const startOffset = segment * keyIndex;
    const endOffset = segment * (keyIndex + 1);
    const delayStart = clamp(
      effectiveStart + startOffset,
      current.percent,
      next.percent,
    );
    const delayEnd = clamp(
      effectiveStart + endOffset,
      delayStart,
      next.percent,
    );

    if (delayStart - current.percent > EPS) {
      pushEntry(adjusted, { percent: delayStart, height: current.height });
    }
    if (delayEnd - delayStart > EPS || Math.abs(delayEnd - delayStart) < EPS) {
      pushEntry(adjusted, { percent: delayEnd, height: next.height });
    }
  }

  pushEntry(adjusted, timeline[timeline.length - 1]);
  return adjusted;
}
